import React, { useCallback, useEffect, useState } from 'react'
import _debounce from 'lodash.debounce'

import './styles.scss'
import { foldersAPI } from 'api'
import { Button, SelectWithLabel } from 'components/Form'
import Input from './Input'
import FolderItem from './FolderItem'
import Modal from './Modal'
import { lang } from 'utils/helpers'
import { getStaticImage } from 'utils/statics'

const FoldersView = () => {
  const [searchValue, setSearchValue] = useState('')
  const [folders, setFolders] = useState([])
  const [isAllFolders, setIsAllFolders] = useState(false)
  const [folderToEdit, setFolderToEdit] = useState('')
  const [isLoadingFolders, setIsLoadingFolders] = useState(false)
  const [foldersError, setFoldersError] = useState('')
  const [isOpenFolderModal, setIsOpenFolderModal] = useState(false)
  const [isLoadingModal, setIsLoadingModal] = useState(false)
  const [modalError, setModalError] = useState('')
  const selectorOptions = [
    { value: 1, label: 'Alfabética AZ' },
    { value: -1, label: 'Alfabética ZA' }
  ]
  const [order, setOrder] = useState(selectorOptions[0])
  const [page, setPage] = useState(0)
  const pageSize = 20

  useEffect(() => {
    getFolders()
  }, [page, order])

  const toggleFolderModal = folderId => {
    if (folderId) setFolderToEdit(folderId)
    if (isOpenFolderModal) {
      if (modalError) setModalError('')
      if (folderToEdit) setFolderToEdit('')
    }
    setIsOpenFolderModal(prevState => !prevState)
  }

  const handleSearch = useCallback(_debounce(text => getFolders({ text }), 250), [])

  const handleOnChange = text => {
    setSearchValue(text)
    handleSearch(text)
  }


  const getFolders = (params = {}) => {
    const { text } = params

    const data = {
      page,
      pageSize,
      order: order.value,
      fields: ['color', 'name', 'listId', 'total_conversations']
    }
    if (text) data.name = text

    setIsLoadingFolders(true)
    foldersAPI.getFolders(data)
      .then(({ data }) => {
        if (!page) {
          setFolders(data)
        } else {
          setFolders(prevState => ([...prevState, ...data]))
        }
        if (data.length < pageSize) setIsAllFolders(true)
      })
      .catch(() => setFoldersError(lang('dashboard_error-getting-folders')))
      .finally(() => setIsLoadingFolders(false))
  }

  const handleSave = ({ name, color }) => {
    if (modalError) setModalError('')
    setIsLoadingModal(true)
    if (!folderToEdit) {
      foldersAPI.createFolder({ name, color })
        .then(({ data: { color, name, listId, _id } }) => {
          const updatedFolders = [...folders]
          updatedFolders.push({  color, name, listId, _id })
          setFolders(updatedFolders)
          toggleFolderModal()
        })
        .catch(({ data }) => {
          if (data.type) {
            setModalError(lang(data.type))
          } else {
            setModalError(lang('dashboard_error-creating-folder'))
          }
        })
        .finally(() => setIsLoadingModal(false))
    } else {
      foldersAPI.updateFolder(({ data: { name, color }, folderId: folderToEdit }))
        .then(() => {
          const updatedFolders = [...folders]
          const folderIndex = updatedFolders.findIndex(({ _id }) => _id === folderToEdit)
          updatedFolders[folderIndex] = { ...updatedFolders[folderIndex], name, color }
          setFolders(updatedFolders)
          toggleFolderModal()
        })
        .catch(() => setModalError(lang('dashboard_error-editing-folder')))
        .finally(() => setIsLoadingModal(false))
    }
  }

  return (
    <div className='folders-view'>
      <div className='folders-view__content'>
        <div className='folders-view__header'>
          <p className='folders-view__title'>{lang('dashboard_folders')}</p>
          <p className='folders-view__subtitle'>{lang('dashboard_folders-config-subtitle')}</p>
          {!foldersError ? (
            <div className='folders-view__filters'>
              <Input
                withSearchIcon
                placeholder={lang('dashboard_search')}
                value={searchValue}
                onChange={handleOnChange}
              />
              <div className='folders-view__selector-container'>
                <label className='folders-view__selector-label'>{lang('dashboard_order-by')}</label>
                <SelectWithLabel
                  placeholder={lang('dashboard_select')}
                  options={selectorOptions}
                  value={order}
                  onChange={setOrder}
                />
              </div>
            </div>
          ) : null}
        </div>
        {!foldersError ? (
          <>
            <div className='folders-view__body'>
              {folders.map((folder, index) => (
                <FolderItem
                  color={folder.color}
                  folderId={folder._id}
                  folders={folders}
                  key={index}
                  name={folder.name}
                  setFolders={setFolders}
                  toggleFolderModal={toggleFolderModal}
                  totalConversations={folder.totalConversations}
                />
              ))}
              {(isLoadingFolders || !isAllFolders) ? (
                <Button
                  clearPrimary
                  className='folders-view__btn folders-view__btn--load'
                  value={lang('dashboard_load-more')}
                  onClick={() => setPage(prevState => prevState + 1)}
                  isSubmitting={isLoadingFolders}
                />
              ) : null}
            </div>
            <div className='folders-view__footer'>
              <Button
                light
                className='folders-view__btn'
                icon={
                  <figure className='folders-view__btn-icon'>
                    <img src={getStaticImage('/icons/orange-brand/add-circle-icon-primary.svg')} alt='Add' />
                  </figure>
                }
                value={lang('dashboard_add-folder')}
                onClick={toggleFolderModal}
              />
            </div>
          </>
        ) : (
          <p className='folders-view__error-text'>{foldersError}</p>
        )}

        {/* Modal */}
        {isOpenFolderModal ? (
          <Modal
            isOpen={isOpenFolderModal}
            toggle={toggleFolderModal}
            data={folders.find(({ _id }) => _id === folderToEdit)}
            onSave={handleSave}
            isLoading={isLoadingModal}
            error={modalError}
          />
        ) : null}
      </div>
    </div>
  )
}

export default FoldersView
